import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import CarouselCubit, { CarouselCubitProps } from "./CarouselCubit";
import { useBloc } from "@blac/react";

const EmblaWrapper = styled.section`
  width: 100%;
  height: 100%;
  --slide-spacing: 0;
  --slide-size: 100%;
`;

const EmblaViewport = styled.div`
  overflow: hidden;
  height: 100%;
`;

const EmblaContainer = styled.div`
  display: flex;
  height: 100%;
  margin: 0 calc(var(--slide-spacing) / -2);
`;

const EmblaSlide = styled.div`
  flex: 0 0 var(--slide-size);
  min-width: 0;
  height: 100%;
  padding: 0 calc(var(--slide-spacing) / 2);
  box-sizing: border-box;

  &:focus {
    outline: none;
  }
`;

const EmblaSlideContent = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export type CarouselProps = {
  id: string;
  children: React.ReactNode[];
  options?: EmblaOptionsType;
  slideSpacing?: string;
  slideSize?: string;
  "aria-label"?: string;
} & CarouselCubitProps;

const Carousel: React.FC<CarouselProps> = ({
  children,
  options,
  slideSpacing,
  slideSize,
  id,
  onStepChange,
  "aria-label": ariaLabel
}) => {
  const [state, cubit] = useBloc(CarouselCubit, {
    id
  });
  cubit.setProps({ id, onStepChange, label: ariaLabel });
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  cubit.setEmblaApi(emblaApi);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prevSelectedIndex = useRef(state.selectedIndex);
  const isInitialMount = useRef(true);
  const slideCount = React.Children.count(children);
  cubit.setTotalSlides(slideCount);

  const slideNames = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && typeof child.props === "object") {
      const childProps = child.props as Record<string, unknown>;
      return childProps["data-carousel-slide-name"] as string | undefined;
    }
    return undefined;
  });
  cubit.setSlideNames(slideNames ?? []);

  /**
   * Focus the newly selected slide when selectedIndex changes
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevSelectedIndex.current = state.selectedIndex;
      return;
    }

    if (prevSelectedIndex.current !== state.selectedIndex) {
      const newSlide = slideRefs.current[state.selectedIndex];
      if (newSlide) {
        newSlide.focus({ preventScroll: true });
      }
      prevSelectedIndex.current = state.selectedIndex;
    }
  }, [state.selectedIndex]);

  return (
    <EmblaWrapper
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      style={
        {
          "--slide-spacing": slideSpacing,
          "--slide-size": slideSize
        } as React.CSSProperties
      }
    >
      <EmblaViewport ref={emblaRef} aria-live="polite" aria-atomic="false">
        <EmblaContainer role="list">
          {React.Children.map(children, (child, index) => (
            <EmblaSlide
              key={index}
              ref={(el) => {
                slideRefs.current[index] = el;
              }}
              role="listitem"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${slideCount}`}
              aria-hidden={index !== state.selectedIndex}
              inert={index !== state.selectedIndex ? true : undefined}
              tabIndex={index === state.selectedIndex ? -1 : undefined}
            >
              <EmblaSlideContent>{child}</EmblaSlideContent>
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </EmblaViewport>
    </EmblaWrapper>
  );
};

export default Carousel;
